import { create } from 'zustand';

import { Member } from '@/entities/vfo/types';

import { sortMembers } from '../helpers';

export type MembersStoreState = {
  isInitialized: boolean;
  members: Member[];
  isLoading: boolean;
};

export type MembersStore = MembersStoreState & {
  setMembers: (members: Member[]) => void;
  addMember: (newMember: Member) => void;
  updateMember: (profileId: number, updates: Omit<Partial<Member>, 'id'>) => void;
  removeMember: (profileId: number) => void;
  setLoading: (isLoading: boolean) => void;
  setInitialized: (isInitialized: boolean) => void;
  clear: () => void;
};

export const useMembersStore = create<MembersStore>((set, get) => ({
  members: [],
  isLoading: false,
  isInitialized: false,

  setMembers: (members) => set({ members }),
  addMember: (newMember) =>
    set(() => {
      const existingIndex = get().members.findIndex((m) => m.profileId === newMember.profileId);
      let updatedMembers: Member[];
      if (existingIndex !== -1) {
        updatedMembers = [...get().members];
        updatedMembers[existingIndex] = newMember;
      } else {
        updatedMembers = [newMember, ...get().members];
      }
      return { members: sortMembers(updatedMembers) };
    }),
  updateMember: (profileId, data) =>
    set(() => {
      const updatedMembers = get().members.map((m) =>
        m.profileId === profileId ? { ...m, ...data } : m,
      );
      return { members: sortMembers(updatedMembers) };
    }),
  removeMember: (profileId) =>
    set(() => ({
      members: get().members.filter((m) => m.profileId !== profileId),
    })),
  setLoading: (isLoading) => set({ isLoading }),
  setInitialized: (isInitialized) => set({ isInitialized }),
  clear: () => set({ members: [], isInitialized: false }),
}));
